/**
 * Vault → Recover my vault (Phase 9c). For a user who lost their recovery phrase and device:
 *   1. choose a NEW vault password + 2FA code → request (this device keeps a one-time key)
 *   2. support checks identity and approves → waiting period (emails at every step; cancel any time)
 *   3. after the owner restores it: enter the new password here → the vault opens on this device
 */
import type { VaultRecoveryInfo } from "@custos/api-client";
import { Badge, Button, Card, CardTitle, Field, Notice, Skeleton, formatDateTime } from "@custos/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { PageHeader } from "../components/AppShell";
import { CodeInput } from "../components/bits";
import { NewPasswordFields, SecretInput } from "../components/VaultBits";
import { api, queryClient } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { useVaultRecovery } from "../lib/queries";
import { finishRecovery, hasPendingRestore, startRecovery, vaultRecoveryKey } from "../lib/vault";
import { useVaultSession } from "../lib/vault-ui";

const STEP_TEXT: Record<string, string> = {
  REQUESTED: "Waiting for support to check your identity",
  APPROVED: "Approved — in the safety waiting period",
  READY: "Ready — finish the recovery below",
};

export function VaultRecoverPage() {
  const vault = useVaultSession();
  const recovery = useVaultRecovery();
  const info = recovery.data;
  const request = info?.request;
  const open = request && ["REQUESTED", "APPROVED", "READY"].includes(request.status) ? request : null;

  return (
    <>
      <PageHeader
        title="Recover my vault"
        subtitle="For when you've lost your recovery phrase and your device."
      />
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        {recovery.isPending || vault.status === "loading" ? (
          <Skeleton className="h-64 rounded-card" />
        ) : recovery.error ? (
          <Notice tone="danger">{errorMessage(recovery.error)}</Notice>
        ) : !info?.available ? (
          <Notice tone="warning">Vault recovery isn't available on this server.</Notice>
        ) : open ? (
          <OpenRequest info={info} request={open} />
        ) : !info.backup?.enabled ? (
          <Notice tone="warning" title="No recovery backup">
            Recovery wasn't turned on for a vault on this account, so support can't restore one. Only your
            recovery phrase or private keys can.{" "}
            <Link to="/vault" className="underline">
              Back to vault
            </Link>
          </Notice>
        ) : (
          <RequestForm info={info} hasVaultHere={vault.status === "locked" || vault.status === "unlocked"} />
        )}
      </div>
    </>
  );
}

function RequestForm({ info, hasVaultHere }: { info: VaultRecoveryInfo; hasVaultHere: boolean }) {
  const [password, setPassword] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!password || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await startRecovery(password, code);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="animate-rise">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <CardTitle>Ask support to recover your vault</CardTitle>
        <ol className="list-decimal pl-5 text-sm text-muted">
          <li>Choose a new vault password for this device and confirm with your 2FA code.</li>
          <li>Support checks it's really you (you may be contacted).</li>
          <li>
            After approval there's a {info.waitHours}-hour safety wait — you're emailed at every step and can
            cancel any time.
          </li>
          <li>
            Then come back here on <b>this device</b> to finish.
          </li>
        </ol>
        {hasVaultHere && (
          <Notice tone="warning">
            This device already has a vault. The recovered vault will replace it.
          </Notice>
        )}
        <NewPasswordFields onChange={setPassword} touched={touched} label="New vault password" />
        <Field label="2FA code" hint="From your authenticator app.">
          {(p) => <CodeInput {...p} value={code} onChange={setCode} />}
        </Field>
        {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
        <Button type="submit" block loading={busy}>
          Request recovery
        </Button>
      </form>
    </Card>
  );
}

function OpenRequest({
  info,
  request,
}: {
  info: VaultRecoveryInfo;
  request: NonNullable<VaultRecoveryInfo["request"]>;
}) {
  const navigate = useNavigate();
  const pending = useQuery({ queryKey: ["vault-pending-restore"], queryFn: hasPendingRestore });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const finish = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await finishRecovery(request.id, password);
      navigate("/vault", { replace: true });
    });
  };

  return (
    <Card className="animate-rise flex flex-col gap-4" data-testid="vault-recovery-request">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Recovery request</CardTitle>
        <Badge tone={request.status === "READY" ? "success" : "warning"}>{request.status}</Badge>
      </div>
      <p className="text-sm text-fg" data-testid="vault-recovery-step">
        {STEP_TEXT[request.status]}
      </p>
      <ul className="text-sm text-muted">
        <li>Requested {formatDateTime(request.requestedAt)}</li>
        {request.readyAt && <li>Can be completed after {formatDateTime(request.readyAt)}</li>}
      </ul>

      {request.status === "READY" &&
        (pending.data ? (
          <form onSubmit={finish} className="flex flex-col gap-4" noValidate>
            <Field label="New vault password" hint="The one you chose when you asked for recovery.">
              {(p) => (
                <SecretInput
                  {...p}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              )}
            </Field>
            <Button type="submit" block loading={busy} disabled={!password}>
              Finish recovery
            </Button>
          </form>
        ) : (
          pending.data === false && (
            <Notice tone="warning">
              Finish on the device (and browser) where you asked for recovery — only it can open the restored
              vault. If you no longer have it, cancel and ask again from this device.
            </Notice>
          )
        ))}

      {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
      <Button
        variant="ghost"
        className="text-danger"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await api.vaultCancelRecovery();
            await queryClient.invalidateQueries({ queryKey: vaultRecoveryKey });
          })
        }
      >
        Cancel this recovery
      </Button>
      <p className="text-xs text-muted">
        Didn't ask for this? Cancel it now and change your password. Waiting period: {info.waitHours} hours.
      </p>
    </Card>
  );
}
